import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import {
    collection,
    getDocs,
    query,
    orderBy,
} from 'firebase/firestore';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import './AdminDashboard.css';

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(ts) {
    if (!ts) return 'N/A';
    // Firestore Timestamp → JS Date
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
}

function formatCurrency(v) {
    if (v == null) return '—';
    return `₱${Number(v).toLocaleString('en-PH')}`;
}

// ── Status constants (must match orders.js ORDER_STATUS values exactly) ───────
const ORDER_STATUS = {
    PENDING_APPROVAL:  'pending approval',
    APPROVED:          'approved',
    AWAITING_PAYMENT:  'awaiting payment confirmation',
    IN_PRODUCTION:     'in production',
    TO_BE_DELIVERED:   'to be delivered',
    DELIVERED:         'delivered',
    CANCELLED:         'cancelled',
};

// ── Build weekly sales buckets for current & last month ───────────────────────
// FIX: reads o.createdAt (Firestore Timestamp) and o.total (not o.orderDate / o.totalAmount)
function buildWeeklyData(orders) {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth();

    const lastMonth = month === 0 ? 11 : month - 1;
    const lastYear  = month === 0 ? year - 1 : year;

    const weekOf = (ts, refYear, refMonth) => {
        if (!ts) return null;
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        if (d.getFullYear() !== refYear || d.getMonth() !== refMonth) return null;
        const day = d.getDate();
        if (day <= 7)  return 0;
        if (day <= 14) return 1;
        if (day <= 21) return 2;
        return 3;
    };

    const currentBuckets = [0, 0, 0, 0];
    const lastBuckets    = [0, 0, 0, 0];

    orders.forEach((o) => {
        // FIX: use o.total (saved by orders.js) instead of o.totalAmount
        const amt = Number(o.total) || 0;
        // FIX: use o.createdAt (saved by orders.js) instead of o.orderDate
        const ts  = o.createdAt;
        if (!ts) return;

        const wi = weekOf(ts, year, month);
        if (wi !== null) { currentBuckets[wi] += amt; return; }

        const wl = weekOf(ts, lastYear, lastMonth);
        if (wl !== null) lastBuckets[wl] += amt;
    });

    return [
        { week: '1st Week', current: currentBuckets[0], last: lastBuckets[0] },
        { week: '2nd Week', current: currentBuckets[1], last: lastBuckets[1] },
        { week: '3rd Week', current: currentBuckets[2], last: lastBuckets[2] },
        { week: '4th Week', current: currentBuckets[3], last: lastBuckets[3] },
    ];
}

// ── sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, colorClass, loading }) {
    return (
        <div className={`dash-stat-card ${colorClass}`}>
            <span className="dash-stat-label">{label}</span>
            <span className="dash-stat-value">{loading ? '—' : value}</span>
        </div>
    );
}

function RefreshIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}

// FIX: status color map now uses actual ORDER_STATUS string values
const STATUS_COLORS = {
    [ORDER_STATUS.PENDING_APPROVAL]:  '#e05252',
    [ORDER_STATUS.APPROVED]:          '#9b59b6',
    [ORDER_STATUS.AWAITING_PAYMENT]:  '#e67e22',
    [ORDER_STATUS.IN_PRODUCTION]:     '#f5a623',
    [ORDER_STATUS.TO_BE_DELIVERED]:   '#5b8ecf',
    [ORDER_STATUS.DELIVERED]:         '#4caf50',
    [ORDER_STATUS.CANCELLED]:         '#999',
};

// FIX: display labels for actual ORDER_STATUS values
function statusLabel(s) {
    const map = {
        [ORDER_STATUS.PENDING_APPROVAL]:  'Pending',
        [ORDER_STATUS.APPROVED]:          'Approved',
        [ORDER_STATUS.AWAITING_PAYMENT]:  'Awaiting Payment',
        [ORDER_STATUS.IN_PRODUCTION]:     'In-Progress',
        [ORDER_STATUS.TO_BE_DELIVERED]:   'To Deliver',
        [ORDER_STATUS.DELIVERED]:         'Completed',
        [ORDER_STATUS.CANCELLED]:         'Cancelled',
    };
    return map[s] || s;
}

const PIE_COLORS = ['#2563eb', '#7c3aed', '#dc2626', '#16a34a', '#d97706', '#0891b2'];

const LOW_STOCK_PRIORITY = (qty) => {
    if (qty <= 10) return { label: 'Critical',    cls: 'dash-priority--critical' };
    if (qty <= 30) return { label: 'Running Out', cls: 'dash-priority--running'  };
    return              { label: 'Low',           cls: 'dash-priority--low'      };
};

// ── main component ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
    const navigate = useNavigate();

    const [orders,     setOrders]     = useState([]);
    const [products,   setProducts]   = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [ordersSnap, productsSnap] = await Promise.all([
                getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
                getDocs(collection(db, 'products')),
            ]);
            setOrders(ordersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setProducts(productsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error('Dashboard fetch error:', err);
        }
        setLoading(false);
    }, [setOrders, setProducts, setLoading]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll, refreshKey]);

    // ── derived data ───────────────────────────────────────────────────────────

    // FIX: filter by actual ORDER_STATUS string values (not 'pending', 'in_production', etc.)
    const pending    = orders.filter((o) => o.status === ORDER_STATUS.PENDING_APPROVAL).length;
    const inProgress = orders.filter((o) => o.status === ORDER_STATUS.IN_PRODUCTION).length;
    const completed  = orders.filter((o) => o.status === ORDER_STATUS.DELIVERED).length;
    const totalOrders = orders.length;

    const recentOrders = orders.slice(0, 5);

    const weeklyData = buildWeeklyData(orders);

    // FIX: Top Products — read from o.orderItems[] (array), not o.product (single string)
    // Each orderItem has: { name, quantity, ... }
    const salesMap = {};
    orders.forEach((o) => {
        const items = o.orderItems;
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
            if (!item.name) return;
            if (!salesMap[item.name]) salesMap[item.name] = 0;
            salesMap[item.name] += Number(item.quantity) || 0;
        });
    });
    const topProducts = Object.entries(salesMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, sales], i) => {
            const prod = products.find((p) => p.name === name);
            return { rank: i + 1, name, sales, price: prod?.startingPrice ?? prod?.price ?? '—' };
        });

    const pieData = topProducts.length > 0
        ? topProducts.map((p) => ({ name: p.name, value: p.sales }))
        : [{ name: 'No data', value: 1 }];

    const LOW_THRESHOLD = 55;
    const lowStockProducts = products
        .filter((p) => (p.stockQuantity ?? 0) <= LOW_THRESHOLD)
        .sort((a, b) => (a.stockQuantity ?? 0) - (b.stockQuantity ?? 0))
        .slice(0, 5);

    function handleRefresh() {
        setRefreshKey((k) => k + 1);
    }

    // ── render ─────────────────────────────────────────────────────────────────

    return (
        <div className="dash-root">
            {/* ── Stat cards ── */}
            <div className="dash-stats">
                <StatCard label="Pending"     value={pending}     colorClass="dash-stat--red"    loading={loading} />
                <StatCard label="In-Progress" value={inProgress}  colorClass="dash-stat--orange" loading={loading} />
                <StatCard label="Completed"   value={completed}   colorClass="dash-stat--green"  loading={loading} />
                <StatCard label="Total Orders" value={totalOrders} colorClass="dash-stat--blue"   loading={loading} />
            </div>

            {/* ── Main grid ── */}
            <div className="dash-grid">

                {/* Monthly Sales chart */}
                <div className="dash-card dash-card--chart">
                    <div className="dash-card-header">
                        <h3 className="dash-card-title">Monthly Sales</h3>
                        <button className="dash-icon-btn" onClick={handleRefresh} title="Refresh">
                            <RefreshIcon />
                        </button>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={weeklyData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false} />
                            <XAxis
                                dataKey="week"
                                tick={{ fontSize: 12, fontFamily: 'Nunito, sans-serif', fill: '#888' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 12, fontFamily: 'Nunito, sans-serif', fill: '#888' }}
                                axisLine={false}
                                tickLine={false}
                                width={55}
                                tickFormatter={(v) => `₱${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                            />
                            <Tooltip
                                contentStyle={{
                                    fontFamily: 'Nunito, sans-serif',
                                    fontSize: 13,
                                    borderRadius: 10,
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                }}
                                formatter={(val) => [`₱${Number(val).toLocaleString('en-PH')}`, '']}
                            />
                            <Legend
                                verticalAlign="top"
                                align="center"
                                iconType="circle"
                                iconSize={12}
                                wrapperStyle={{ fontFamily: 'Nunito, sans-serif', fontSize: 13, paddingBottom: 12 }}
                                formatter={(value) => value === 'current' ? 'Current Month' : 'Last Month'}
                            />
                            <Line
                                type="monotone"
                                dataKey="current"
                                stroke="#4caf50"
                                strokeWidth={2.5}
                                dot={{ r: 5, fill: '#4caf50', strokeWidth: 0 }}
                                activeDot={{ r: 7 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="last"
                                stroke="#f5a623"
                                strokeWidth={2.5}
                                dot={{ r: 5, fill: '#f5a623', strokeWidth: 0 }}
                                activeDot={{ r: 7 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Right column */}
                <div className="dash-right-col">

                    {/* Recent Orders */}
                    <div className="dash-card">
                        <div className="dash-card-header">
                            <h3 className="dash-card-title">Recent Orders</h3>
                            <div className="dash-card-header-icons">
                                <button className="dash-icon-btn" onClick={handleRefresh} title="Refresh"><RefreshIcon /></button>
                                <button className="dash-icon-btn" onClick={() => navigate('/admin/orders/list')} title="View all"><ExternalIcon /></button>
                            </div>
                        </div>
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', maxWidth: '100%' }}>
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Total</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={5} className="dash-table__empty">Loading...</td></tr>
                                ) : recentOrders.length === 0 ? (
                                    <tr><td colSpan={5} className="dash-table__empty">No orders yet.</td></tr>
                                ) : (
                                    recentOrders.map((o) => (
                                        <tr key={o.id}>
                                            {/* FIX: show o.orderId (e.g. JG-20240324-00001) not o.id slice */}
                                            <td className="dash-order-id">{o.orderId || `#${o.id.slice(0, 6).toUpperCase()}`}</td>
                                            <td>{o.customerName || '—'}</td>
                                            <td>
                                                <span
                                                    className="dash-status"
                                                    style={{ color: STATUS_COLORS[o.status] || '#888' }}
                                                >
                                                    {statusLabel(o.status)}
                                                </span>
                                            </td>
                                            {/* FIX: use o.total (not o.totalAmount) */}
                                            <td className="dash-total">{formatCurrency(o.total)}</td>
                                            {/* FIX: use o.createdAt (not o.orderDate) */}
                                            <td className="dash-date">{formatDate(o.createdAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        </div>
                    </div>

                    {/* Top Products */}
                    <div className="dash-card">
                        <div className="dash-card-header">
                            <h3 className="dash-card-title">Top Products</h3>
                            <div className="dash-card-header-icons">
                                <button className="dash-icon-btn" onClick={handleRefresh} title="Refresh"><RefreshIcon /></button>
                                <button className="dash-icon-btn" onClick={() => navigate('/admin/products/manage')} title="View all"><ExternalIcon /></button>
                            </div>
                        </div>
                        <div className="dash-top-products">
                            <div className="dash-pie-wrap">
                                <PieChart width={120} height={120}>
                                    <Pie
                                        data={pieData}
                                        cx={55}
                                        cy={55}
                                        innerRadius={0}
                                        outerRadius={55}
                                        dataKey="value"
                                        strokeWidth={1}
                                        stroke="#fff"
                                    >
                                        {pieData.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            fontFamily: 'Nunito, sans-serif',
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                </PieChart>
                            </div>
                            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', maxWidth: '100%', minWidth: 0, flex: 1 }}>
                            <table className="dash-table dash-table--products">
                                <thead>
                                    <tr>
                                        <th>Rank</th>
                                        <th>Name</th>
                                        <th>Sales</th>
                                        <th>Price <span className="dash-th-sub">(Per Unit)</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={4} className="dash-table__empty">Loading...</td></tr>
                                    ) : topProducts.length === 0 ? (
                                        <tr><td colSpan={4} className="dash-table__empty">No data yet.</td></tr>
                                    ) : (
                                        topProducts.map((p) => (
                                            <tr key={p.rank}>
                                                <td className="dash-rank">
                                                    <span
                                                        className="dash-rank-dot"
                                                        style={{ background: PIE_COLORS[(p.rank - 1) % PIE_COLORS.length] }}
                                                    />
                                                    #{p.rank}
                                                </td>
                                                <td>{p.name}</td>
                                                <td>{p.sales}</td>
                                                <td>{p.price !== '—' ? `₱${Number(p.price).toLocaleString('en-PH')}` : '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>

                    {/* Low Stock Alert */}
                    <div className="dash-card">
                        <div className="dash-card-header">
                            <h3 className="dash-card-title">Low Stock Alert</h3>
                            <div className="dash-card-header-icons">
                                <button className="dash-icon-btn" onClick={handleRefresh} title="Refresh"><RefreshIcon /></button>
                                <button className="dash-icon-btn" onClick={() => navigate('/admin/products/inventory')} title="View all"><ExternalIcon /></button>
                            </div>
                        </div>
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', maxWidth: '100%' }}>
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Priority</th>
                                    <th>Name</th>
                                    <th>Remaining Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={3} className="dash-table__empty">Loading...</td></tr>
                                ) : lowStockProducts.length === 0 ? (
                                    <tr><td colSpan={3} className="dash-table__empty">All products well-stocked!</td></tr>
                                ) : (
                                    lowStockProducts.map((p) => {
                                        const priority = LOW_STOCK_PRIORITY(p.stockQuantity ?? 0);
                                        return (
                                            <tr key={p.id}>
                                                <td>
                                                    <span className={`dash-priority ${priority.cls}`}>
                                                        {priority.label}
                                                    </span>
                                                </td>
                                                <td>{p.name || '—'}</td>
                                                <td>{p.stockQuantity ?? '—'}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}